import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BellRing, Fingerprint } from 'lucide-react'
import Modal from './Modal'
import { fetchContent, text } from '../api/content'
import { markWelcomeSeen } from '../api/auth'
import { SESSION_KEY, useSession } from '../useSession'
import { useDialogsStandDown } from '../dialogStandDown'

/**
 * The one-off hello (D-651).
 *
 * **Owner, from the pilot:** *"After first opening the app, a modal telling
 * first-time users they can set up notifications and biometrics in account
 * settings."*
 *
 * **Both things it names are already built and nobody knew.** Push arrives
 * switched off per device (D-530) and a passkey has to be enrolled on the
 * phone that will use it (D-320) — so both are opt-in by design, both live
 * behind a settings screen nobody has a reason to open, and the pilot's
 * teachers went a fortnight without either. The dialog is not a feature; it is
 * the sentence that was missing.
 *
 * **A modal, which this codebase otherwise avoids**, because the whole point is
 * that a panel further down the page is the thing that was already there and
 * already unread. It is dismissible, it appears once ever, and it never returns
 * — a welcome that can come back is an advertisement.
 *
 * **Marked seen on dismissal, not on render.** If the write fails she sees it
 * again next time, which is the harmless direction; stamping it as it appears
 * would spend her one showing on a dialog a page reload might have swallowed.
 */
export default function WelcomeDialog() {
  const { session } = useSession()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  /*
   * **Closed here as well as on the server**, because the session is cached and
   * the dialog must go the instant she taps rather than when a refetch lands.
   * The server flag is what stops it coming back tomorrow.
   */
  const [closed, setClosed] = useState(false)

  // Not over attendance work, and not on a load that began at a notification link:
  // the same rule the what's-new dialog keeps (D-731, amends D-651).
  const standDown = useDialogsStandDown()

  const seen = useMutation({
    mutationFn: markWelcomeSeen,
    /*
     * Either way the dialog is gone for this visit; the refetch is what makes
     * that true for the next one, and a failed write simply shows it again.
     *
     * `SESSION_KEY` rather than a written-out `['session']`: they agree today,
     * and a key that ever gains a segment would turn the copy into a silent
     * no-op invalidate that nothing would report.
     */
    onSettled: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
  })

  const dismiss = () => {
    setClosed(true)
    seen.mutate()
  }

  if (closed || standDown || session?.showWelcome !== true) return null

  return (
    <Modal
      onDismiss={dismiss}
      labelledBy="welcome-title"
      describedBy="welcome-body"
      panelClassName="w-full max-w-[24rem] rounded-2xl border border-line bg-surface p-5 shadow-2xl"
    >
      <>
        <h2 id="welcome-title" className="font-display text-xl">
          {text(content, 'welcome.heading', 'Welcome to the studio app')}
        </h2>

        <p id="welcome-body" className="mt-2 text-[13px] text-ink-2">
          {text(
            content,
            'welcome.body',
            'Two things worth setting up while you are here: turn on notifications so you hear about ' +
              'cancellations and reminders, and sign in with your fingerprint or face instead of a password. ' +
              'Both live in your account settings, and you can change them any time.',
          )}
        </p>

        {/*
          **Icons for the two things, and nothing else.** The sentence above is
          Tonya's to reword (A-16), so nothing here may restate it — these only
          give the reader something to recognise on the settings screen when she
          gets there.
        */}
        <ul className="mt-3.5 flex flex-col gap-2 text-[12.5px] text-ink-3">
          <li className="flex items-center gap-2">
            <BellRing size={15} strokeWidth={2.25} aria-hidden="true" className="shrink-0" />
            Notifications
          </li>
          <li className="flex items-center gap-2">
            <Fingerprint size={15} strokeWidth={2.25} aria-hidden="true" className="shrink-0" />
            Fingerprint or face
          </li>
        </ul>

        <div className="mt-4 flex flex-col gap-2">
          {/*
            **It takes her there rather than telling her where it is.** A
            dialog naming a screen and leaving her to find it is the fault shape
            D-312 records: the link that lands somewhere unable to answer, one
            step earlier. `/account` is not gated on anything — both settings
            are per account — so this can never offer a screen its reader is
            refused.
          */}
          <button
            type="button"
            onClick={() => {
              dismiss()
              navigate('/account#sign-in')
            }}
            className="min-h-[44px] w-full rounded-lg border border-accent bg-accent-soft px-3 py-2 text-[13.5px] font-bold text-accent-2"
          >
            {text(content, 'welcome.settings', 'Open account settings')}
          </button>

          <button
            type="button"
            onClick={dismiss}
            className="min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13.5px] font-semibold text-ink-2"
          >
            {text(content, 'welcome.dismiss', 'Maybe later')}
          </button>
        </div>
      </>
    </Modal>
  )
}
